import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./distribuzioneRendimenti1y.module.scss";
import { formatDateTooltip } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

const DistribuzioneRendimenti1y = ({
  data,
  colorBarChart = "#103976",
  isCompareTitle = null,
  isCompareSubtitle = null,
}) => {
  const { t } = useTranslation();

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const staticColorBarChart = colorBarChart
    ? colorBarChart
    : colorsConfig.FinecoBlueDark;
  const formatValue = (value) => {
    if (value === null || value === undefined) return "0";
    return formatItalianNumber(Number(value), 1);
  };

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  if (!data?.analisi_backtest?.distribuzione_rendimenti_mensili) return null;
  const distribuzioneRendimenti1yData =
    data.analisi_backtest.distribuzione_rendimenti_mensili
      .distribuzione_rendimenti_mensili;
  const formattedData = distribuzioneRendimenti1yData.map((item) => ({
    ...item,
    value: Math.round(item.value),
  }));
  const lineUp =
    data.analisi_backtest.distribuzione_rendimenti_mensili
      .max_guadagno_potenziale_95;
  const lineDown =
    data.analisi_backtest.distribuzione_rendimenti_mensili
      .max_perdita_potenziale_95;

  if (formattedData.length === 0) {
    return null;
  }
  // Extract unique years from the data
  const uniqueYears = Array.from(
    new Set(formattedData.map((item) => new Date(item.date).getFullYear())),
  ).sort((a, b) => Number(a) - Number(b));

  // Create a mapping from year to the first date in that year
  const yearToDateMap: Record<number, string> = {};

  uniqueYears.forEach((year: number) => {
    const dateItem = formattedData.find(
      (item) => new Date(item.date).getFullYear() === year,
    );
    if (dateItem) {
      yearToDateMap[year] = dateItem.date;
    }
  });
  // Define ticks using the first date of each year
  const xAxisTicks = formattedData
    .filter((item, index, self) => {
      const year = new Date(item.date).getFullYear();
      return (
        index === self.findIndex((i) => new Date(i.date).getFullYear() === year)
      );
    })
    .map((item) => item.date); // <- keep it as full date string

  if (data.length === 0) {
    return null;
  }

  // Custom tooltip component with TypeScript interface
  interface TooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

  const CustomTooltip: React.FC<TooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          backgroundColor: colorsConfig.finecoWhite || "#fff",
          padding: "10px",
          border: `1px solid ${colorsConfig.FinecoGrayLight1 || "#ccc"}`,
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p
          style={{
            marginBottom: "5px",
            fontWeight: "bold",
            fontFamily: "Arial",
            fontSize: "12px",
            color: colorsConfig.finecoBlack || "#000",
          }}
        >
          {formatDateTooltip(label)}
        </p>
        {payload.map((entry, index) => (
          <p
            key={`tooltip-${index}`}
            style={{
              margin: "3px 0",
              fontFamily: "Arial",
              fontSize: "12px",
              color: entry.color,
            }}
          >
            {`${entry.name}: ${formatItalianNumber(Number(entry.value), 1)}%`}
          </p>
        ))}
      </div>
    );
  };

  if (!isCompareTitle) {
  }
  return (
    <div className={styles.sectionContainer}>
      {!isCompareTitle && !isCompareSubtitle && (
        <h2 className={styles.sectionTitle}>
          {t("portfolio.distribuzioneRendimenti1y")}
        </h2>
      )}
      {isCompareTitle && (
        <h2 className={styles.sectionTitle}>{isCompareTitle}</h2>
      )}
      {isCompareSubtitle && (
        <h2 className={styles.sectionTitle}>{isCompareSubtitle}</h2>
      )}

      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={370} debounce={500}>
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 10, left: -25, bottom: 10 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            {/* Reference line at 0% */}
            <ReferenceLine
              y={0}
              stroke={colorsConfig.finecoBlack}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
            <XAxis
              dataKey="date"
              ticks={xAxisTicks}
              tickFormatter={(value: string) => {
                const date = new Date(value);
                return isNaN(date.getTime())
                  ? value
                  : date.getFullYear().toString();
              }}
              tick={chartCopy}
              tickLine={false}
              dy={10}
              strokeDasharray="none"
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <YAxis
              tickFormatter={formatValue}
              tickLine={false}
              tick={chartCopy}
              domain={[
                (dataMin) => {
                  const minCandidate = Math.min(dataMin, lineDown);
                  return Math.floor(minCandidate * 1.25);
                },
                (dataMax) => Math.ceil(dataMax * 1.25),
              ]}
              interval={0}
              tickCount={10}
              allowDataOverflow={false}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />

            <ReferenceLine
              y={lineUp}
              stroke={colorsConfig.Green}
              strokeWidth={1}
            />
            <ReferenceLine
              y={lineDown}
              stroke={colorsConfig.red}
              strokeWidth={1}
            />
            <Bar
              dataKey="value"
              name={"Rendimenti"}
              fill={staticColorBarChart}
              barSize={2}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
        <div className={styles.customLegend}>
          <div className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: colorsConfig.red }}
            />
            <span>Max Perdita</span>
          </div>
          <div className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: colorsConfig.Green }}
            />
            <span>Max Guadagno</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistribuzioneRendimenti1y;
